import { escapeHtml } from '../lib/html.js';
import { statusPillClass } from '../lib/status.js';
import { listShopifyVendas } from '../lib/shopify.js';
import { fmtValor, moedaBase } from '../lib/moeda.js';
import { buildHref } from '../lib/router.js';
import { paginate, CATALOG_PAGE_SIZE } from '../lib/pagination.js';
import { renderPager } from './pager.js';

let vendas = [];
let loaded = false;
let loadedMoeda = '';
let loading = false;
let loadError = '';
const filters = { loja: '', status: '' };

export function setVendasFilter(name, value) {
  filters[name] = value || '';
}

export function invalidateVendas() {
  loaded = false;
}

function valorNaBase(item) {
  if (item.total_base != null) return Number(item.total_base) || 0;
  if (item.total_brl != null && moedaBase() === 'BRL') return Number(item.total_brl) || 0;
  if (String(item.currency || '').toUpperCase() === 'BRL') return Number(item.total) || 0;
  return 0;
}

function diaLocal(iso) {
  const dt = new Date(iso || '');
  if (Number.isNaN(dt.getTime())) return '';
  const mes = String(dt.getMonth() + 1).padStart(2, '0');
  const dia = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mes}-${dia}`;
}

export function resumoVendasPeriodo(de, ate) {
  if (!loaded || loadedMoeda !== moedaBase()) return null;
  const pagos = vendas.filter((item) => {
    const dia = diaLocal(item.created_at_shopify);
    return item.financial_status === 'paid' && !item.cancelled_at && dia >= de && dia <= ate;
  });
  return {
    vendido: pagos.reduce((soma, item) => soma + valorNaBase(item), 0),
    pedidos: pagos.length,
    erro: loadError,
  };
}

export async function refreshVendas() {
  loading = true;
  loadError = '';
  try {
    vendas = await listShopifyVendas({ limit: '500', moeda: moedaBase() });
    loadedMoeda = moedaBase();
    loaded = true;
  } catch (err) {
    loadError = err.message || 'Não foi possível carregar as vendas.';
    vendas = [];
    loaded = true;
    loadedMoeda = moedaBase();
  } finally {
    loading = false;
  }
  return vendas;
}

function moneyBase(value) {
  return fmtValor(value);
}

function money(value, currency = 'BRL') {
  try {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: currency || 'BRL' });
  } catch {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}

function fxHint(venda) {
  if (venda.total_brl == null) return `${venda.items_count || 0} un.`;
  if (String(venda.currency || '').toUpperCase() === moedaBase()) return `${venda.items_count || 0} un. · já em ${moedaBase()}`;
  const rate = Number(venda.fx_rate);
  const quoted = rate > 0 ? rate.toLocaleString('pt-BR', { maximumFractionDigits: 4 }) : '—';
  return `${moneyBase(venda.total_base != null ? venda.total_base : venda.total_brl)} · câmbio ${quoted} em ${venda.fx_quoted_at || '—'}`;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function payLabel(status) {
  const map = {
    paid: 'paga',
    pending: 'pendente',
    authorized: 'autorizada',
    refunded: 'reembolsada',
    voided: 'anulada',
    partially_paid: 'parcialmente paga',
    partially_refunded: 'parcialmente reembolsada',
  };
  return map[String(status || '').toLowerCase()] || status || '—';
}

function shipLabel(status) {
  const map = {
    fulfilled: 'enviado',
    partial: 'parcial',
    unfulfilled: 'não enviado',
    restocked: 'reposto',
  };
  const key = String(status || '').toLowerCase();
  if (!key) return 'não enviado';
  return map[key] || status;
}

function filtered() {
  return vendas.filter((venda) => {
    if (filters.loja && venda.shop_domain !== filters.loja) return false;
    if (filters.status === 'paid') {
      return venda.financial_status === 'paid' && !venda.cancelled_at;
    }
    if (filters.status === 'unpaid') {
      return venda.financial_status !== 'paid' || venda.cancelled_at;
    }
    if (filters.status && venda.financial_status !== filters.status) return false;
    return true;
  });
}

export function renderVendas({ page = 1, onNeedRefresh } = {}) {
  if (loaded && loadedMoeda !== moedaBase()) loaded = false;
  if (!loaded && !loading) {
    refreshVendas().then(() => onNeedRefresh?.());
  }
  if (!loaded || loading) {
    return `<div class="empty-state">Carregando vendas…</div>`;
  }
  if (loadError) {
    return `<div class="empty-state">${escapeHtml(loadError)}</div>`;
  }

  const list = filtered();
  const pg = paginate(list, page, CATALOG_PAGE_SIZE);
  const lojas = [...new Set(vendas.map((item) => item.shop_domain).filter(Boolean))].sort();
  const brlOf = (item) => {
    if (item.total_base != null) return Number(item.total_base) || 0;
    if (item.total_brl != null && moedaBase() === 'BRL') return Number(item.total_brl) || 0;
    if (String(item.currency || '').toUpperCase() === 'BRL') return Number(item.total) || 0;
    return 0;
  };
  const pagos = list.filter((item) => item.financial_status === 'paid' && !item.cancelled_at);
  const vendido = pagos.reduce((sum, item) => sum + brlOf(item), 0);
  const bruto = list.reduce((sum, item) => sum + brlOf(item), 0);
  const ticket = pagos.length ? vendido / pagos.length : 0;

  const rows = pg.total
    ? pg.items.map((venda) => {
        const items = venda.items || [];
        const itemsHint = items.length
          ? items.slice(0, 3).map((item) => `${item.quantity}× ${item.title || item.name}`).join(' · ')
          : 'Itens entram na próxima sincronização';
        const extra = items.length > 3 ? ` +${items.length - 3}` : '';
        const utm = [venda.utm_source, venda.utm_campaign].filter(Boolean).join(' / ') || '—';
        const place = [venda.city, venda.province, venda.country].filter(Boolean).join(' · ');
        return `<tr>
          <td>
            <div class="cell-title">${escapeHtml(venda.order_name || `#${venda.shopify_order_id}`)}</div>
            <div class="cell-sub">${escapeHtml(venda.loja_nome || venda.shop_domain)}</div>
          </td>
          <td>
            <div class="cell-title">${escapeHtml(venda.customer_name || venda.customer_email || '—')}</div>
            <div class="cell-sub">${escapeHtml(venda.customer_email || place || 'sem e-mail')}</div>
          </td>
          <td>
            <span class="pill ${statusPillClass(payLabel(venda.financial_status))}">${escapeHtml(payLabel(venda.financial_status))}</span>
            <div class="cell-sub">${escapeHtml(shipLabel(venda.fulfillment_status))}</div>
          </td>
          <td>
            <div class="cell-title">${escapeHtml(money(venda.total, venda.currency))}</div>
            <div class="cell-sub">${escapeHtml(fxHint(venda))}</div>
          </td>
          <td>
            <div class="cell-title">${escapeHtml(utm)}</div>
            <div class="cell-sub">${escapeHtml(venda.gateway || venda.source_name || '—')}</div>
          </td>
          <td>
            <div class="cell-sub">${escapeHtml(itemsHint)}${extra}</div>
          </td>
          <td class="cell-sub">${escapeHtml(fmtWhen(venda.created_at_shopify))}</td>
        </tr>`;
      }).join('')
    : `<tr><td colspan="7" class="empty-state">Nenhuma venda importada. Conecte a loja e sincronize o histórico.</td></tr>`;

  return `
    <div class="fat-stats" style="margin-bottom:16px;flex-wrap:wrap">
      <div class="mini-stat">
        <div class="lbl">Pedidos</div>
        <div class="val">${list.length}</div>
        <div class="cell-sub">${pagos.length} pago(s)</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Vendido pago (${moedaBase()})</div>
        <div class="val">${escapeHtml(moneyBase(vendido))}</div>
        <div class="cell-sub">${escapeHtml(moneyBase(bruto))} bruto convertido</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Ticket médio</div>
        <div class="val">${escapeHtml(moneyBase(ticket))}</div>
        <div class="cell-sub">só pedidos pagos</div>
      </div>
    </div>
    <div class="table-toolbar">
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <select class="filter" id="vendas-loja">
          <option value="">Todas as lojas</option>
          ${lojas.map((loja) => `<option value="${escapeHtml(loja)}" ${filters.loja === loja ? 'selected' : ''}>${escapeHtml(loja)}</option>`).join('')}
        </select>
        <select class="filter" id="vendas-status">
          <option value="" ${!filters.status ? 'selected' : ''}>Todos os status</option>
          <option value="paid" ${filters.status === 'paid' ? 'selected' : ''}>Pagas</option>
          <option value="unpaid" ${filters.status === 'unpaid' ? 'selected' : ''}>Não pagas</option>
          <option value="pending" ${filters.status === 'pending' ? 'selected' : ''}>Pendentes</option>
          <option value="refunded" ${filters.status === 'refunded' ? 'selected' : ''}>Reembolsadas</option>
        </select>
      </div>
      <span class="cell-sub">${pg.total} pedido(s)</span>
    </div>
    <div class="panel-box"><div class="table-scroll"><table>
      <thead><tr><th>Pedido</th><th>Cliente</th><th>Status</th><th>Valor</th><th>Origem</th><th>Itens</th><th>Quando</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div></div>
    ${pg.total ? renderPager({
      id: 'vendas',
      page: pg.page,
      totalPages: pg.totalPages,
      from: pg.from,
      to: pg.to,
      total: pg.total,
      label: 'pedido(s)',
      hrefFor: (n) => buildHref({ section: 'vendas', page: n }),
    }) : ''}`;
}
